// @ts-strict-ignore
import isNumber from 'lodash/isNumber';
import mergeWith from 'lodash/mergeWith';

import {formatConciseDuration} from 'common/util/date';
import {
  isConversionOrRetentionRateEntry,
  isConversionOrRetentionRateMetric,
  isFunnelTimeToConvertMetric,
  isFunnelTimeToConvertShowClause,
  isLiftComparisonBookmark,
  showClausesAreConversionOrRetentionRateMetric,
} from 'common/report/insights/models/utils';
import {
  InsightsBookmarkParams,
  InsightsChartParams,
  InsightsQueryResults,
  ShowClause,
} from 'common/report/insights/util/types';
import {EventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {DataFormatType} from 'common/components/data-table/types';
import {TableRow} from 'common/components/table/types';
import {PropertyDisplayOptions} from 'apps/lexicon/src/models/property-definition';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {LineChartYAxisOptions} from 'common/charts/visualization/line-chart/types';
import {EntryWithDisplay} from 'common/query-builder/query-entry-section/types';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {ModelStatus} from 'common/util/model-resource/model-resource';
import {ChartType} from 'common/report/constants';

type MetricFormatterFunction = (value: number) => number | string;

function coreMetricFormatterFunction<T>(
  paramsOrClause: T | null,
  isConversionOrRetentionCheck: (paramsOrClause: T) => boolean,
  isFunnelTimeToConvertCheck: (paramsOrClause: T) => boolean,
): MetricFormatterFunction {
  return (value: number): number | string => {
    if (!paramsOrClause) {
      return value;
    }
    if (isConversionOrRetentionCheck(paramsOrClause)) {
      // Just multiply by 100. We'll delegate to code higher up to handle potential
      // user-specified overrides on decimal precision, etc.
      return value * 100;
    } else if (isFunnelTimeToConvertCheck(paramsOrClause)) {
      return formatConciseDuration(value);
    }
    return value;
  };
}

/**
 * * @param bookmarkParams
 * @deprecated Only use this if you only have bookmark data and don't have a
 * specific show clause. E.g. formatting for axes vs a specific metric. The show
 * clause variant of this function will give a more accurate result.

 * @returns
 */
export function metricFormatterFunctionForBookmarkParams(bookmarkParams: InsightsBookmarkParams | InsightsChartParams) {
  return coreMetricFormatterFunction(bookmarkParams, isConversionOrRetentionRateMetric, isFunnelTimeToConvertMetric);
}

export function metricFormatterFunctionForShowClause(showClause: ShowClause) {
  return coreMetricFormatterFunction(
    showClause,
    (c) => showClausesAreConversionOrRetentionRateMetric([c]),
    isFunnelTimeToConvertShowClause,
  );
}

function coreGetMetricFormattingDefaults<T>(
  paramsOrClause: T,
  isPercentCheck: (paramsOrClause: T) => boolean,
  isTable?: boolean,
): Partial<PropertyDisplayOptions> {
  if (!paramsOrClause) {
    return {};
  }
  if (isPercentCheck(paramsOrClause)) {
    return {
      suffix: `%`,
      abbrev: !isTable,
    };
  }
  if (isTable) {
    return {abbrev: false};
  }
  return {};
}

/**
 * Given a bookmark or chart params object, returns the appropriate defaults
 * for formatting metrics to be displayed.
 * @param bookmarkParams
 * @param isForAxis Axes are uniquely handled because if we have a lift comparison
 * for instance we want to format JUST the axis with a "%" suffix.
 * @deprecated Only use this if you only have bookmark data and don't have a
 * specific show clause. E.g. formatting for axes vs a specific metric. The show
 * clause variant of this function will give a more accurate result.
 * @returns
 */
export function getMetricFormattingDefaultsFromChartParams(
  bookmarkParams: InsightsBookmarkParams | InsightsChartParams,
  isForAxis = false,
): Partial<PropertyDisplayOptions> {
  return coreGetMetricFormattingDefaults(
    bookmarkParams,
    (params) => isConversionOrRetentionRateMetric(params) || (isForAxis && isLiftComparisonBookmark(params)),
    bookmarkParams.displayOptions?.chartType === ChartType.Table,
  );
}

export function getMetricFormattingDefaultsFromShowClause(showClause: ShowClause): Partial<PropertyDisplayOptions> {
  return coreGetMetricFormattingDefaults(showClause, (c) => showClausesAreConversionOrRetentionRateMetric([c]));
}

export function getMetricFormattingDefaultsFromQueryEntry(
  entry: EntryWithDisplay,
  chartType?: ChartType,
): Partial<PropertyDisplayOptions> {
  return coreGetMetricFormattingDefaults(entry, isConversionOrRetentionRateEntry, chartType === ChartType.Table);
}

export function getMetricKeyToShowClauseMap(
  bookmarkParams: InsightsBookmarkParams | InsightsChartParams,
  results: InsightsQueryResults,
): Record<string, ShowClause> {
  return bookmarkParams.sections.show.reduce(
    (acc, section, index) => {
      const metricKey = results.meta.report_sections.show[index].metric_key;
      acc[metricKey] = section;
      return acc;
    },
    {} as Record<string, ShowClause>,
  );
}

export const getLineChartYAxisOptionsFromPropertyDisplayOptions = (
  propertyDisplayOptions: Partial<PropertyDisplayOptions>,
): LineChartYAxisOptions => {
  return {
    axisUnit: {
      prefix: propertyDisplayOptions.prefix,
      suffix: propertyDisplayOptions.suffix,
      precision: propertyDisplayOptions.precision,
    },
  };
};

export const getDisplayOptionsFromLineChartYAxisOptions = (
  lineChartYAxisOptions: LineChartYAxisOptions,
): Partial<PropertyDisplayOptions> => {
  return {
    abbrev: lineChartYAxisOptions.axisUnit?.abbrev,
    prefix: lineChartYAxisOptions.axisUnit?.prefix,
    suffix: lineChartYAxisOptions.axisUnit?.suffix,
    precision: lineChartYAxisOptions.axisUnit?.precision,
  };
};

/**
 * Correctly merges multiple PropertyDisplayOptions or LineChartYAxisOptions
 * objects together. Just
 * merging them together w/o this function can result in unwanted behavior.
 * @param optionsList
 * @returns
 */
export function mergeDisplayOptions<T extends PropertyDisplayOptions | LineChartYAxisOptions>(
  ...optionsList: Array<Partial<T>>
): Partial<T> {
  function merger(objValue, srcValue, key) {
    // Only override with non empty prefix/suffix values
    if ([`prefix`, `suffix`].includes(key) && !srcValue) {
      return objValue;
    }
    // Delegate to lodash's default behavior
    return undefined;
  }
  return mergeWith.apply(null, [...optionsList, merger]);
}

export function displayOptionsHaveChanged({
  current,
  defaults,
}: {
  current: Partial<PropertyDisplayOptions>;
  defaults: Partial<PropertyDisplayOptions>;
}): boolean {
  // We care about current values that aren't undefined and aren't exactly equal.
  // With the exception of prefix/suffix in which case we want to treat the
  // empty string the same as undefined (not set).
  return Object.keys(current).some((key) => {
    if ([`prefix`, `suffix`].includes(key)) {
      return Boolean(current[key]) !== Boolean(defaults[key]);
    }
    return current[key] !== undefined && current[key] !== defaults[key];
  });
}

/**
 * Pulls out the display options defined for the relevant property and metric
 * and then returns the final values to be used for formatting.
 * @param showClause
 * @param eventPropertyDefinitionsStore
 * @param defaultOptions Optional default values to start with and then have overridden
 * by property values.
 * @param overrideOptions Optional values to override all other values.
 * @returns
 */
export function getFinalDisplayOptions(
  showClause: ShowClause,
  eventPropertyDefinitionsStore: EventPropertyDefinitionsStore,
  metricsResource: MetricsResource,
  defaultOptions: Partial<PropertyDisplayOptions> = {},
  overrideOptions: Partial<PropertyDisplayOptions> = {},
): Partial<PropertyDisplayOptions> {
  const options = {prefix: ``, suffix: ``, abbrev: true, precision: null, ...defaultOptions};

  if (showClause?.measurement && `property` in showClause.measurement && showClause.measurement.property) {
    const property = showClause.measurement.property;
    let propertyDefinition = null;
    if (property.customPropertyId) {
      propertyDefinition = customPropertiesStore.value[property.customPropertyId];
    } else {
      const propertyName = property.name;
      propertyDefinition = propertyName instanceof Array ? null : eventPropertyDefinitionsStore.value[propertyName];
    }

    mergeDisplayOptions(options, propertyDefinition?.displayOptions);
    // NOTE: We only care about the formula IF the user hasn't defined any
    // display options for the metric themselves. If they have user-defined settings
    // we'll just use those.
  } else if (showClause && !showClause?.display && `formula` in showClause && showClause.formula.id) {
    const model = metricsResource.getOne(showClause.formula.id);
    if (model.status === ModelStatus.Success) {
      const formulaDisplay = model.value.definition.display;
      mergeDisplayOptions(options, formulaDisplay);
    }
  }
  mergeDisplayOptions(options, showClause?.display, overrideOptions);

  return options;
}

/**
 * If one value in a column has a decimal format, all values in that column
 * should be formatted as decimals. This function scans all rows in a table to
 * see that the given column field has a decimal value.
 *
 * Types that already have decimals (percent, time) should be left alone.
 * @param dataFormatType
 * @param rows
 * @param field
 * @returns Returns the correct data format type, updated to decimal if needed.
 */
export function checkForDecimalDataType(
  dataFormatType: DataFormatType,
  rows: TableRow[],
  field: string,
): DataFormatType {
  if ([DataFormatType.Percent, DataFormatType.Time].includes(dataFormatType)) {
    return dataFormatType;
  }

  return rows.some((row) => {
    const value = row[field];
    return isNumber(value) && value % 1 !== 0;
  })
    ? DataFormatType.Decimal
    : dataFormatType;
}
